import {
  CheckCircleIcon,
  CaretDownIcon,
  DownloadSimpleIcon,
  GitBranchIcon,
  GitDiffIcon,
  ImageIcon,
  MagnifyingGlassIcon,
  PlugsConnectedIcon,
  CopyIcon,
  ShieldCheckIcon,
  TerminalWindowIcon,
  WarningIcon,
  XCircleIcon,
} from '@phosphor-icons/react'
import { useEffect, useMemo, useState } from 'react'
import type { PlanState, TaskMessage, TaskState, TeamAgent, TeamRoomState } from '../../task-runtime/types'
import { selectActivityChanges, selectActivityPresentation, selectActivityTiming, type ActivityPresentationItem, type ActivityChange } from './activityPresentation'

function ActionIcon({ type }: { type: Extract<ActivityPresentationItem, { kind: 'action' }>['actionType'] }) {
  if (type === 'command' || type === 'test') return <TerminalWindowIcon />
  if (type === 'file') return <GitDiffIcon />
  if (type === 'plan') return <GitBranchIcon />
  if (type === 'search') return <MagnifyingGlassIcon />
  if (type === 'tool') return <PlugsConnectedIcon />
  if (type === 'review') return <ShieldCheckIcon />
  if (type === 'image' || type === 'browser') return <ImageIcon />
  return <CheckCircleIcon />
}

function WorkingIndicator({ text }: { text: string }) {
  return (
    <div className="activity-working" role="status" aria-live="polite" aria-label="Current activity">
      <span className="activity-working-pulse" aria-hidden="true" />
      <span className="activity-working-text" aria-hidden="true">{text}</span>
      <span className="sr-only">{text}</span>
    </div>
  )
}

function formatDuration(elapsedMs: number) {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (hours) return `${hours}h ${minutes}m ${seconds}s`
  if (minutes) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}

function WorkedDivider({ elapsedMs }: { elapsedMs: number }) {
  return <div className="activity-worked-divider" role="status"><span>Worked for {formatDuration(elapsedMs)}</span></div>
}

function ChangeSummaryActivity({ changes }: { changes: ActivityChange[] }) {
  if (!changes.length) return null
  return (
    <section className="activity-change-summary" aria-label="Changed files">
      <div className="activity-change-summary-head">
        <span className="activity-change-summary-icon" aria-hidden="true"><GitDiffIcon /></span>
        <strong>{changes.length} file{changes.length === 1 ? '' : 's'} changed</strong>
      </div>
      <ul>
        {changes.map((change) => <li key={change.path}>
          <code>{change.path}</code>
          <span className="activity-change-stats"><b>+{change.additions}</b><em>−{change.deletions}</em></span>
        </li>)}
      </ul>
    </section>
  )
}

function MessageActivity({ item }: { item: Extract<ActivityPresentationItem, { kind: 'message' }> }) {
  return (
    <article className={`activity-stream-message is-${item.role}`}>
      <span className="activity-stream-avatar">{item.role === 'user' ? 'You' : '!'}</span>
      <p>{item.text}</p>
    </article>
  )
}

function DecisionActivity({ item }: { item: Extract<ActivityPresentationItem, { kind: 'decision' }> }) {
  return <p className={`activity-decision ${item.streaming ? 'is-streaming' : ''}`}>{item.text}</p>
}

function ActionActivity({ item }: { item: Extract<ActivityPresentationItem, { kind: 'action' }> }) {
  const detail = item.detail?.trim()
  const expandable = Boolean(detail && detail.length > 150)
  return (
    <div className={`activity-stream-row activity-action is-${item.actionType}`}>
      <span className="activity-stream-icon" aria-hidden="true"><ActionIcon type={item.actionType} /></span>
      <span className="activity-stream-copy"><strong>{item.text}</strong>{detail && (expandable
        ? <details className="activity-stream-detail"><summary>Show output</summary><small>{detail}</small></details>
        : <small>{detail}</small>)}</span>
      {item.status && <span className="activity-stream-status">{item.status}</span>}
    </div>
  )
}

function ExceptionalActivity({ item }: { item: Extract<ActivityPresentationItem, { kind: 'approval' | 'warning' | 'error' }> }) {
  const isApproval = item.kind === 'approval'
  const detail = item.kind === 'approval' ? item.detail : undefined
  return (
    <div className={`activity-stream-row activity-exception is-${item.kind}`} role={isApproval ? 'alert' : undefined}>
      <span className="activity-stream-icon" aria-hidden="true">{isApproval ? <ShieldCheckIcon /> : item.kind === 'warning' ? <WarningIcon /> : <XCircleIcon />}</span>
      <span className="activity-stream-copy"><strong>{item.text}</strong>{detail && <small>{detail}</small>}</span>
    </div>
  )
}

function CompletionActivity({ item }: { item: Extract<ActivityPresentationItem, { kind: 'completion' }> }) {
  const [copied, setCopied] = useState(false)
  const [createdAt] = useState(() => Date.now())
  const copy = async () => {
    try {
      if (!navigator.clipboard) return
      await navigator.clipboard.writeText(item.text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1_400)
    } catch {
      setCopied(false)
    }
  }
  return <div className={`activity-completion is-${item.status}`}>
    <div className="activity-completion-copy">
      <p>{item.text}</p>
      <div className="activity-message-actions" aria-label="Message actions">
        <button type="button" className="activity-message-action" aria-label="Copy message" title={copied ? 'Copied' : 'Copy'} onClick={() => void copy()}>
          <CopyIcon aria-hidden="true" />
          <span className="sr-only">{copied ? 'Copied' : 'Copy'}</span>
        </button>
        <time dateTime={new Date(createdAt).toISOString()}>{new Intl.DateTimeFormat('tr-TR', { hour: '2-digit', minute: '2-digit' }).format(createdAt)}</time>
      </div>
    </div>
  </div>
}

type PlanActions = {
  onApprove?: () => void
  onCancel?: () => void
  onPause?: () => void
  onResume?: () => void
  onRequestChanges?: (instruction: string) => void
  onAgentMessage?: (message: string, agentId?: string) => void
}

const PLAN_TERMINAL = new Set(['completed', 'failed', 'cancelled', 'escalated'])
const PLAN_APPROVAL = new Set(['awaiting_approval', 'draft', 'ready'])
const PLAN_RUNNING = new Set(['running', 'in_progress', 'running_step', 'testing', 'reviewing'])

function planLabel(status: string) {
  return status.replaceAll('_', ' ')
}

function planDocument(plan: PlanState) {
  const lines = [
    `# ${plan.title}`,
    '',
    '## Özet',
    plan.explanation || 'Bu plan, mevcut proje bağlamı incelendikten sonra hazırlandı.',
    '',
    '## Adımlar',
  ]
  for (const [index, step] of plan.steps.entries()) {
    lines.push(`${index + 1}. ${step.title}`)
    if (step.description) lines.push(`   - ${step.description}`)
    if (step.files?.length) lines.push(`   - Dosyalar: ${step.files.join(', ')}`)
    if (step.commands?.length) lines.push(`   - Komutlar: ${step.commands.join(' · ')}`)
    if (step.checks) lines.push(`   - Kontrol: ${step.checks}`)
  }
  return lines.join('\n')
}

function TeamRoomSummary({ team }: { team: TeamRoomState }) {
  const initials = (agent: TeamAgent) => (agent.displayName || agent.title || agent.role || 'A').split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase()
  return (
    <section className="team-room-summary" aria-label="Team Room">
      <div className="team-room-summary-head">
        <div className="team-room-avatar-stack" aria-label="Team agents">
        {team.agents.map((agent) => {
          const avatarAsset = typeof agent.avatar?.asset === 'string' ? agent.avatar.asset : ''
          return <span className={`team-room-avatar is-${agent.state || 'queued'}`} title={`${agent.displayName || agent.title || agent.role || 'Agent'} · ${agent.statusSummary || agent.state || 'queued'}`} aria-label={`${agent.displayName || agent.title || agent.role || 'Agent'}, ${agent.statusSummary || agent.state || 'queued'}`} key={agent.id} style={{ '--team-accent': typeof agent.avatar?.accent === 'string' ? agent.avatar.accent : '#7bb6d8', '--team-bg': typeof agent.avatar?.background === 'string' ? agent.avatar.background : '#233442' } as React.CSSProperties}>{avatarAsset ? <img src={avatarAsset} alt="" width={28} height={28} /> : initials(agent)}</span>
        })}
        </div>
        <span><strong>Team Room</strong><small>{team.agents.length} ajan · {team.messages.length} konuşma</small></span>
      </div>
      {team.messages.length > 0 && <div className="team-room-transcript" aria-label="Team conversation">
        {team.messages.slice(-6).map((message) => <div className="team-room-message" key={message.id}><strong>{message.fromLabel || message.from.id}</strong><span>{message.text}</span></div>)}
      </div>}
      {(team.decisions.length > 0 || team.conflicts.length > 0) && <div className="team-room-ledger"><span>{team.decisions.length} decision{team.decisions.length === 1 ? '' : 's'}</span><span className={team.conflicts.some((item) => item.status === 'open') ? 'is-conflict' : ''}>{team.conflicts.length} conflict{team.conflicts.length === 1 ? '' : 's'}</span></div>}
    </section>
  )
}

function PlanActivity({ plan, onApprove, onCancel, onPause, onResume, onRequestChanges }: { plan: PlanState } & PlanActions) {
  const [instruction, setInstruction] = useState('')
  const [editing, setEditing] = useState(false)
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const canApprove = PLAN_APPROVAL.has(plan.status)
  const canPause = PLAN_RUNNING.has(plan.status)
  const canResume = plan.status === 'paused'
  const canCancel = !PLAN_TERMINAL.has(plan.status) && plan.status !== 'paused'
  const submitChanges = () => {
    const value = instruction.trim()
    if (!value || !onRequestChanges) return
    onRequestChanges(value)
    setInstruction('')
    setEditing(false)
  }
  const documentText = planDocument(plan)
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(documentText)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1_400)
    } catch {
      setCopied(false)
    }
  }
  const download = () => {
    const blob = new Blob([documentText], { type: 'text/markdown;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${plan.title.toLocaleLowerCase('tr-TR').replace(/[^a-z0-9ğüşıöç]+/gi, '-').replace(/^-|-$/g, '') || 'plan'}.md`
    document.body.append(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }
  return (
    <section className={`activity-plan is-${plan.status}`} aria-label="Implementation plan">
      <div className="activity-plan-head">
        <button type="button" className="activity-plan-summary" aria-expanded={open} aria-label={`${open ? 'Planı kapat' : 'Planı aç'}: ${plan.title}`} onClick={() => setOpen((value) => !value)}>
          <span className="activity-plan-icon" aria-hidden="true"><GitBranchIcon /></span>
          <span className="activity-plan-title"><span className="activity-plan-kicker">PLAN BELGESİ</span><strong>{plan.title}</strong><small>{plan.steps.length} adım · {plan.owner?.agent || 'Planlayıcı'}{plan.owner?.model ? ` · ${plan.owner.model}` : ''}</small></span>
          <CaretDownIcon className={open ? 'is-open' : ''} aria-hidden="true" />
        </button>
        <div className="activity-plan-tools" aria-label="Plan actions">
          <button type="button" aria-label="Planı indir" title="Planı indir" onClick={download}><DownloadSimpleIcon /></button>
          <button type="button" aria-label="Planı kopyala" title={copied ? 'Kopyalandı' : 'Planı kopyala'} onClick={() => void copy()}><CopyIcon /></button>
        </div>
      </div>
      {!open && <p className="activity-plan-preview">{plan.explanation || 'Plan hazır. Ayrıntıları ve onay düğmesini görmek için kartı aç.'}</p>}
      {open && <div className="activity-plan-body">
      {plan.explanation && <p className="activity-plan-explanation">{plan.explanation}</p>}
      <ol className="activity-plan-steps">
        {plan.steps.slice(0, 40).map((step, index) => <li key={step.id} className={`activity-plan-step is-${step.status} ${plan.activeStepId === step.id ? 'is-active' : ''}`}>
          <span className="activity-plan-step-marker">{step.status === 'completed' ? '✓' : step.status === 'failed' ? '!' : index + 1}</span>
          <div><strong>{step.title}</strong>{step.description && <p>{step.description}</p>}{(step.files?.length || step.commands?.length || step.checks || step.owner?.model || step.review) ? <details className="activity-plan-step-details"><summary>Teknik ayrıntılar</summary><div>{step.owner?.model && <span>Model: {step.owner.model}</span>}{step.checks && <span>Kontrol: {step.checks}</span>}{step.review && <span>İnceleme: {step.review}</span>}{step.files?.length ? <span>Dosyalar: {step.files.slice(0, 6).join(' · ')}</span> : null}{step.commands?.length ? <span>Komutlar: {step.commands.slice(0, 3).join(' · ')}</span> : null}</div></details> : null}</div>
        </li>)}
      </ol>
      {(plan.checks || plan.review?.summary) && <div className="activity-plan-footer">{plan.checks && <span>Checks: {plan.checks}</span>}{plan.review?.summary && <span>Review: {plan.review.summary}</span>}</div>}
      {(onApprove || onCancel || onPause || onResume || onRequestChanges) && <div className="activity-plan-actions">
        {canApprove && onApprove && <button type="button" onClick={onApprove}>Run this plan</button>}
        {canPause && onPause && <button type="button" className="is-muted" onClick={onPause}>Pause</button>}
        {canResume && onResume && <button type="button" onClick={onResume}>Resume</button>}
        {canCancel && onCancel && <button type="button" className="is-muted" onClick={onCancel}>Cancel</button>}
        {canApprove && onRequestChanges && !editing && <button type="button" className="is-muted" onClick={() => setEditing(true)}>Change plan</button>}
        {canApprove && onRequestChanges && editing && <div className="activity-plan-request"><input autoFocus value={instruction} onChange={(event) => setInstruction(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); submitChanges() } if (event.key === 'Escape') setEditing(false) }} placeholder="Tell the planner what to change…" aria-label="Request plan changes" /><button type="button" className="is-muted" disabled={!instruction.trim()} onClick={submitChanges}>Revise plan</button></div>}
      </div>}
      </div>}
    </section>
  )
}

export function ActivityTimeline({ state, plan: planOverride, hidePlan = false, fallbackMessages = [], isRunning = false, onApprove, onCancel, onPause, onResume, onRequestChanges, onAgentMessage }: { state?: TaskState; plan?: PlanState; hidePlan?: boolean; fallbackMessages?: TaskMessage[]; isRunning?: boolean } & PlanActions) {
  const plan = hidePlan ? undefined : planOverride ?? state?.plan
  const presentationState = state && isRunning && !['completed', 'failed', 'cancelled', 'interrupted'].includes(state.status)
    ? { ...state, status: 'running' as const, running: true }
    : state
  const presentation = useMemo(() => presentationState ? selectActivityPresentation({ state: presentationState, plan, fallbackMessages }) : [], [fallbackMessages, plan, presentationState])
  const [now, setNow] = useState(() => Date.now())
  const timing = useMemo(() => presentationState ? selectActivityTiming(presentationState, now) : null, [now, presentationState])
  const changes = useMemo(() => presentationState ? selectActivityChanges(presentationState) : [], [presentationState])
  const visibleStream = useMemo(() => {
    const stream = presentation.filter((item) => item.kind !== 'completion')
    // Keep the DOM bounded during multi-hour tasks while retaining the latest
    // activity, which is the useful context at the bottom of the transcript.
    return stream.length > 240 ? stream.slice(-240) : stream
  }, [presentation])

  useEffect(() => {
    if (!timing?.active) return undefined
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [timing?.active])

  if ((!state || presentation.length === 0) && !plan) return null
  const completed = presentation.filter((item) => item.kind === 'completion')
  return (
    <section className="activity-timeline" aria-label="Task activity">
      {visibleStream.map((item) => {
        if (item.kind === 'working') return <WorkingIndicator key={item.id} text={item.text} />
        if (item.kind === 'message') return <MessageActivity key={item.id} item={item} />
        if (item.kind === 'decision') return <DecisionActivity key={item.id} item={item} />
        if (item.kind === 'action') return <ActionActivity key={item.id} item={item} />
        if (item.kind === 'approval' || item.kind === 'warning' || item.kind === 'error') return <ExceptionalActivity key={item.id} item={item} />
        return null
      })}
      {plan?.team && <TeamRoomSummary team={plan.team} />}
      {plan && <PlanActivity plan={plan} onApprove={onApprove} onCancel={onCancel} onPause={onPause} onResume={onResume} onRequestChanges={onRequestChanges} onAgentMessage={onAgentMessage} />}
      {timing && !timing.active && timing.startedAt !== undefined && <WorkedDivider elapsedMs={timing.elapsedMs} />}
      {completed.map((item) => <CompletionActivity key={item.id} item={item} />)}
      {timing && !timing.active && <ChangeSummaryActivity changes={changes} />}
    </section>
  )
}
